import { useState } from 'react';
import { RefreshCw, Download, Filter, X } from 'lucide-react';
import { AgentLog, LogOperation } from '../../types';
import { LogItem } from './LogItem';
import { AgentFilter } from './AgentFilter';
import { LogDetail } from './LogDetail';
import { Spinner } from '../Common';

interface ActivityFeedProps {
  logs: AgentLog[];
  loading?: boolean;
  filters?: {
    workspace?: string;
    agent?: string;
    operation?: LogOperation;
  };
  onFilterChange: (filters: {
    workspace?: string;
    agent?: string;
    operation?: LogOperation;
  }) => void;
  onRefresh: () => void;
  onExport?: (format: 'json' | 'csv') => void;
}

export function ActivityFeed({
  logs,
  loading,
  filters = {},
  onFilterChange,
  onRefresh,
  onExport,
}: ActivityFeedProps) {
  const [selectedLog, setSelectedLog] = useState<AgentLog | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  const hasActiveFilters =
    filters.workspace || filters.agent || filters.operation;

  const uniqueAgents = [...new Set(logs.map((l) => l.agentId))];
  const uniqueWorkspaces = [...new Set(logs.map((l) => l.workspace))];

  return (
    <div className="flex flex-col h-full bg-white border rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="font-semibold text-gray-900">Agent Activity</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1 px-3 py-1.5 text-sm border rounded-lg ${
              hasActiveFilters
                ? 'bg-primary-50 border-primary-300 text-primary-700'
                : 'hover:bg-gray-50'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filter
            {hasActiveFilters && (
              <span className="ml-1 w-2 h-2 bg-primary-500 rounded-full" />
            )}
          </button>
          {onExport && (
            <div className="relative group">
              <button className="flex items-center gap-1 px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50">
                <Download className="w-4 h-4" />
                Export
              </button>
              <div className="absolute right-0 mt-1 w-32 bg-white border rounded-lg shadow-lg py-1 hidden group-hover:block z-10">
                <button
                  onClick={() => onExport('json')}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50"
                >
                  Export JSON
                </button>
                <button
                  onClick={() => onExport('csv')}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50"
                >
                  Export CSV
                </button>
              </div>
            </div>
          )}
          <button
            onClick={onRefresh}
            disabled={loading}
            className="p-1.5 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
          >
            <RefreshCw
              className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
            />
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="px-4 py-3 border-b bg-gray-50">
          <AgentFilter
            agents={uniqueAgents}
            workspaces={uniqueWorkspaces}
            selectedAgent={filters.agent}
            selectedWorkspace={filters.workspace}
            selectedOperation={filters.operation}
            onAgentChange={(agent) =>
              onFilterChange({ ...filters, agent })
            }
            onWorkspaceChange={(workspace) =>
              onFilterChange({ ...filters, workspace })
            }
            onOperationChange={(operation) =>
              onFilterChange({ ...filters, operation })
            }
            onClear={() => onFilterChange({})}
          />
        </div>
      )}

      {/* Active filters display */}
      {hasActiveFilters && !showFilters && (
        <div className="px-4 py-2 border-b bg-gray-50 flex items-center gap-2 flex-wrap">
          {filters.workspace && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-white border rounded text-sm">
              Workspace: {filters.workspace}
              <button
                onClick={() =>
                  onFilterChange({ ...filters, workspace: undefined })
                }
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          {filters.agent && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-white border rounded text-sm">
              Agent: {filters.agent}
              <button
                onClick={() =>
                  onFilterChange({ ...filters, agent: undefined })
                }
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          {filters.operation && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-white border rounded text-sm">
              Operation: {filters.operation}
              <button
                onClick={() =>
                  onFilterChange({ ...filters, operation: undefined })
                }
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
        </div>
      )}

      {/* Log list */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Spinner />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <p>No activity logs found</p>
            {hasActiveFilters && (
              <button
                onClick={() => onFilterChange({})}
                className="text-primary-600 hover:underline mt-2"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          logs.map((log) => (
            <LogItem
              key={log.id}
              log={log}
              onClick={() => setSelectedLog(log)}
            />
          ))
        )}
      </div>

      {/* Log detail modal */}
      {selectedLog && (
        <LogDetail log={selectedLog} onClose={() => setSelectedLog(null)} />
      )}
    </div>
  );
}
